import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { ChevronLeft, ChevronRight, Check, Pencil, Plus, Trash2, Undo2, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const JOURS = ["dim.", "lun.", "mar.", "mer.", "jeu.", "ven.", "sam."];
const MOIS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

export const hm = (iso) => (iso ? iso.slice(11, 16) : "");
export const fmtDuree = (min) => {
  if (min == null) return "";
  const m = Math.abs(Math.round(min));
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}`;
};
const dayOf = (iso) => new Date(`${iso}T12:00:00`);
const fmtEcart = (e) => (e == null || e === 0 ? "" : `${e > 0 ? "+" : "−"}${Math.abs(e)} min`);
export const moisLabel = (m) => { const [y, mo] = m.split("-"); return `${MOIS[Number(mo) - 1]} ${y}`; };
export const shiftMois = (m, delta) => {
  const [y, mo] = m.split("-").map(Number);
  const d = new Date(y, mo - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
export const currentMois = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };

// Couleur d'une heure : vert = approuvée, rouge = hors tolérance (±5 min) non approuvée.
function heureClass(p, hors) {
  if (p.etat === "valide") return "text-emerald-600 font-semibold";
  if (hors) return "text-red-600 font-semibold";
  return "text-foreground";
}

const ETAT = {
  en_cours: { label: "En cours", cls: "bg-sky-100 text-sky-700" },
  oubli: { label: "Sortie manquante", cls: "bg-red-100 text-red-700" },
  a_valider: { label: "À valider", cls: "bg-red-100 text-red-700" },
  valide: { label: "Validé", cls: "bg-emerald-100 text-emerald-700" },
  conforme: { label: "Conforme", cls: "bg-slate-100 text-slate-600" },
};

function Pointage({ p }) {
  const nuit = p.heure_depart && p.heure_depart.slice(0, 10) !== p.heure_arrivee.slice(0, 10);
  return { nuit };
}

function PointageDialog({ cible, userId, onClose, onSaved }) {
  // cible : { jour, pointage? , prevu? }
  const { jour, pointage, prevu } = cible;
  const [arr, setArr] = useState(pointage ? pointage.heure_arrivee.slice(0, 16) : `${jour}T${prevu?.debut || "09:00"}`);
  const [dep, setDep] = useState(pointage?.heure_depart ? pointage.heure_depart.slice(0, 16) : "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const body = { heure_arrivee: `${arr}:00`, heure_depart: dep ? `${dep}:00` : null };
      if (pointage) await api.patch(`/pointage/${pointage.id}`, body);
      else await api.post("/pointage/manuel", { user_id: userId, ...body });
      toast.success("Pointage enregistré et validé");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="pointage-edit-dialog">
        <DialogHeader><DialogTitle>{pointage ? "Modifier le pointage" : "Ajouter un pointage"} — {dayOf(jour).toLocaleDateString("fr-FR")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <label className="block text-sm">Entrée
            <Input type="datetime-local" value={arr} onChange={(e) => setArr(e.target.value)} data-testid="pointage-edit-arrivee" />
          </label>
          <label className="block text-sm">Sortie <span className="text-muted-foreground">(laisser vide = session ouverte ; la date peut être le lendemain pour un travail de nuit)</span>
            <Input type="datetime-local" value={dep} onChange={(e) => setDep(e.target.value)} data-testid="pointage-edit-depart" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button disabled={busy || !arr} onClick={save} data-testid="pointage-edit-save">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HorairesDialog({ jour, userId, prevu, onClose, onSaved }) {
  const [debut, setDebut] = useState(prevu?.debut || "09:00");
  const [fin, setFin] = useState(prevu?.fin || "17:00");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.get("/planning", { params: { user_id: userId, date_from: jour, date_to: jour } });
      const body = { user_id: userId, date: jour, heure_debut: debut, heure_fin: fin };
      if (r.data.length) await api.patch(`/planning/${r.data[0].id}`, body, { params: { force: true } });
      else await api.post("/planning", body);
      toast.success("Horaire prévu modifié");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="horaires-edit-dialog">
        <DialogHeader><DialogTitle>Horaire prévu — {dayOf(jour).toLocaleDateString("fr-FR")}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm">Début<Input type="time" value={debut} onChange={(e) => setDebut(e.target.value)} data-testid="horaires-debut" /></label>
          <label className="text-sm">Fin<Input type="time" value={fin} onChange={(e) => setFin(e.target.value)} data-testid="horaires-fin" /></label>
        </div>
        <p className="text-xs text-muted-foreground">Une fin avant le début = travail de nuit (se termine le lendemain).</p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button disabled={busy} onClick={save} data-testid="horaires-save">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Feuille de temps mensuelle (style ADP). `feuille` = réponse de
 * /pointage/mes-pointages ou /pointage/feuille. `manager` active l'édition.
 */
export function FeuilleTemps({ feuille, mois, onMois, manager = false, userId, onChanged }) {
  const [dlg, setDlg] = useState(null);
  const [hdlg, setHdlg] = useState(null);
  const today = new Date().toISOString().slice(0, 10);

  const act = async (fn, msg) => {
    try { await fn(); if (msg) toast.success(msg); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!feuille) return <p className="text-sm text-muted-foreground p-4">Chargement…</p>;

  return (
    <div className="rounded-xl border border-border bg-card" data-testid="feuille-temps">
      <div className="flex items-center justify-between gap-2 p-3 border-b border-border flex-wrap">
        <div className="flex items-center gap-1">
          <Button size="icon" variant="outline" onClick={() => onMois(shiftMois(mois, -1))} data-testid="feuille-prev"><ChevronLeft size={16} /></Button>
          <span className="font-heading font-semibold px-3 capitalize min-w-[9rem] text-center" data-testid="feuille-mois">{moisLabel(mois)}</span>
          <Button size="icon" variant="outline" onClick={() => onMois(shiftMois(mois, 1))} data-testid="feuille-next"><ChevronRight size={16} /></Button>
        </div>
        <div className="flex items-center gap-3 text-sm flex-wrap">
          <span data-testid="feuille-total">Total compté : <b>{fmtDuree(feuille.total_minutes)}</b> h</span>
          {feuille.nb_a_valider > 0 && (
            <span className="text-red-600" data-testid="feuille-a-valider">{feuille.nb_a_valider} à valider ({fmtDuree(feuille.minutes_a_valider)} h)</span>
          )}
          {manager && feuille.nb_a_valider > 0 && (
            <Button size="sm" className="gap-1" data-testid="feuille-valider-tout"
              onClick={() => act(() => api.post("/pointage/valider-mois", { user_id: userId, mois }), "Pointages validés")}>
              <Check size={14} />Tout valider
            </Button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs text-muted-foreground">
            <tr className="text-left">
              <th className="px-3 py-2">Date</th>
              <th className="px-2 py-2">Horaire prévu</th>
              <th className="px-2 py-2">Entrée</th>
              <th className="px-2 py-2">Sortie</th>
              <th className="px-2 py-2">Écart</th>
              <th className="px-2 py-2 text-right">Durée</th>
              <th className="px-2 py-2 text-right">Par jour</th>
              <th className="px-2 py-2 text-right">Cumul</th>
              <th className="px-2 py-2">État</th>
              {manager && <th className="px-2 py-2 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {feuille.jours.map((row) => {
              const d = dayOf(row.date);
              const dow = d.getDay();
              const weekend = dow === 0 || dow === 6;
              const isToday = row.date === today;
              const bg = isToday ? "bg-amber-50" : weekend ? "bg-slate-50" : "";
              const prevuTxt = row.prevu ? `${row.prevu.debut} – ${row.prevu.fin}${row.prevu.nuit ? " (+1)" : ""}` : "";
              const lignes = row.pointages.length ? row.pointages : [null];
              return lignes.map((p, i) => {
                const { nuit } = p ? Pointage({ p }) : { nuit: false };
                return (
                  <tr key={`${row.date}-${i}`} className={`border-t border-border ${bg}`} data-testid={`feuille-row-${row.date}`}>
                    <td className="px-3 py-1.5 whitespace-nowrap">
                      {i === 0 && <span className={weekend ? "text-muted-foreground" : ""}>{JOURS[dow]} {String(d.getDate()).padStart(2, "0")}/{String(d.getMonth() + 1).padStart(2, "0")}</span>}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-muted-foreground">
                      {i === 0 && (
                        <span className="inline-flex items-center gap-1">
                          {prevuTxt || (row.conge ? "" : "—")}
                          {manager && (
                            <button className="opacity-60 hover:opacity-100" title="Modifier l'horaire prévu" data-testid={`feuille-horaire-${row.date}`}
                              onClick={() => setHdlg({ jour: row.date, prevu: row.prevu })}><CalendarClock size={13} /></button>
                          )}
                        </span>
                      )}
                    </td>
                    {p ? (
                      <>
                        <td className={`px-2 py-1.5 whitespace-nowrap ${heureClass(p, p.entree_hors)}`} data-testid={`feuille-entree-${row.date}`}>{hm(p.heure_arrivee)}</td>
                        <td className={`px-2 py-1.5 whitespace-nowrap ${p.heure_depart ? heureClass(p, p.sortie_hors) : "text-red-600 font-semibold"}`} data-testid={`feuille-sortie-${row.date}`}>
                          {p.heure_depart ? <>{hm(p.heure_depart)}{nuit && <sup className="ml-0.5 text-[10px]">+1</sup>}</> : (p.etat === "oubli" ? "manquante" : "…")}
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap text-xs">
                          {[p.ecart_entree_min != null && p.entree_hors ? `entrée ${fmtEcart(p.ecart_entree_min)}` : "", p.sortie_hors ? `sortie ${fmtEcart(p.ecart_sortie_min)}` : "", p.hors_planning ? "hors planning" : ""].filter(Boolean).join(" · ")}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{p.duree_minutes != null ? fmtDuree(p.duree_minutes) : ""}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-medium">{i === lignes.length - 1 && row.total_minutes ? fmtDuree(row.total_minutes) : ""}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">{i === lignes.length - 1 ? fmtDuree(row.cumul_minutes) : ""}</td>
                        <td className="px-2 py-1.5">
                          <span className={`text-xs rounded px-1.5 py-0.5 ${ETAT[p.etat]?.cls || ""}`} data-testid={`feuille-etat-${row.date}`}>{ETAT[p.etat]?.label || p.etat}</span>
                          {p.modifie_manuellement && <span className="ml-1 text-[10px] text-muted-foreground" title={`Modifié par ${p.modifie_par || ""}`}>modifié</span>}
                        </td>
                        {manager && (
                          <td className="px-2 py-1.5 text-right whitespace-nowrap">
                            {p.etat === "a_valider" && p.heure_depart && (
                              <Button size="icon" variant="ghost" title="Valider" data-testid={`feuille-valider-${p.id}`}
                                onClick={() => act(() => api.post(`/pointage/${p.id}/valider`), "Validé")}><Check size={15} className="text-emerald-600" /></Button>
                            )}
                            {p.etat === "valide" && (
                              <Button size="icon" variant="ghost" title="Annuler la validation" onClick={() => act(() => api.post(`/pointage/${p.id}/annuler-validation`))}><Undo2 size={15} /></Button>
                            )}
                            <Button size="icon" variant="ghost" title="Modifier" data-testid={`feuille-edit-${p.id}`} onClick={() => setDlg({ jour: row.date, pointage: p, prevu: row.prevu })}><Pencil size={15} /></Button>
                            <Button size="icon" variant="ghost" title="Supprimer" onClick={() => window.confirm("Supprimer ce pointage ?") && act(() => api.delete(`/pointage/${p.id}`), "Supprimé")}><Trash2 size={15} className="text-red-600" /></Button>
                          </td>
                        )}
                      </>
                    ) : (
                      <>
                        <td className="px-2 py-1.5" colSpan={6}>
                          {row.conge ? <span className="text-purple-600 font-medium">Congé{row.conge.type_conge ? ` (${row.conge.type_conge})` : ""}</span>
                            : row.absent ? <span className="text-red-600 font-medium">Absent — non pointé</span> : ""}
                        </td>
                        <td />
                        {manager && (
                          <td className="px-2 py-1.5 text-right">
                            <Button size="icon" variant="ghost" title="Ajouter un pointage" data-testid={`feuille-add-${row.date}`} onClick={() => setDlg({ jour: row.date, prevu: row.prevu })}><Plus size={15} /></Button>
                          </td>
                        )}
                      </>
                    )}
                  </tr>
                );
              });
            })}
          </tbody>
        </table>
      </div>
      <div className="flex gap-4 flex-wrap p-3 text-xs text-muted-foreground border-t border-border">
        <span><b className="text-red-600">Rouge</b> : hors de l'horaire prévu (±{feuille.tolerance_min} min), en attente d'approbation</span>
        <span><b className="text-emerald-600">Vert</b> : approuvé par le gestionnaire</span>
        <span><sup>+1</sup> : sortie le lendemain (travail de nuit)</span>
      </div>

      {dlg && <PointageDialog cible={dlg} userId={userId} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); onChanged?.(); }} />}
      {hdlg && <HorairesDialog {...hdlg} userId={userId} onClose={() => setHdlg(null)} onSaved={() => { setHdlg(null); onChanged?.(); }} />}
    </div>
  );
}
